import { axiosClient } from '@/utils/axios-client'
import type { CreateUser, NestUser, User } from '@/types/user'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { type UUID } from 'crypto'

export const useUserStore = defineStore('user', () => {
  const currentUser: Ref<User | null> = ref(null)
  const currentNestUser: Ref<NestUser | null> = ref(null)

  async function fetchCurrentUser() {
    if (currentUser.value) return currentUser.value

    const response = await axiosClient.request<User>({
      url: `/auth/api/current-user`,
      method: 'GET',
    })

    currentUser.value = response.data
    return currentUser.value
  }

  async function fetchMe() {
    if (currentNestUser.value) return currentNestUser.value

    const response = await axiosClient.request<NestUser>({
      url: 'users/me',
      method: 'GET',
    })

    currentNestUser.value = response.data
    return currentNestUser.value
  }

  async function assignModules(userId: string, modules: number[]) {
    const response = await axiosClient.request({
      url: `/auth/api/user/${userId}/modules`,
      data: modules,
      method: 'POST',
    })

    return response
  }

  async function fetchAll(customerId: UUID) {
    const response = await axiosClient.request<NestUser[]>({
      url: '/users',
      method: 'GET',
      params: {
        customerId,
      },
    })

    return response.data
  }

  async function fetchUser(id: UUID) {
    const response = await axiosClient.request<NestUser>({
      url: `/users/${id}`,
      method: 'GET',
    })

    return response.data
  }

  async function create(user: CreateUser) {
    const me = await fetchMe()

    await axiosClient.request({
      url: '/users',
      method: 'POST',
      data: {
        ...user,
        customerId: me.customerId,
      },
    })
  }

  async function update(user: NestUser) {
    await axiosClient.request({
      url: `/users/${user.id}`,
      method: 'PUT',
      data: user,
    })
  }

  async function resetPassword(email: string) {
    await axiosClient.request({
      url: `/auth/forgot-password`,
      method: 'POST',
      data: { email },
    })
  }

  async function deleteUser(id: UUID) {
    await axiosClient.request({
      url: `/users/${id}`,
      method: 'DELETE',
    })
  }

  return {
    currentUser,
    currentNestUser,
    create,
    update,
    deleteUser,
    fetchMe,
    fetchCurrentUser,
    fetchUser,
    fetchAll,
    resetPassword,
    assignModules,
  }
})
